import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import ReactDOM from "react-dom";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import validator from "validator";
import classNames from "classnames";

import { StatusCode } from "api/protocols";
import {
  GetGiftCard,
  GetGiftCardHistory,
  IGiftCardLog,
  PrintGiftCard,
  PutDisableGiftCard,
  PutEnableGiftCard,
  PutGiftCard,
  PrintGiftCardLog,
  PutGiftCardEmail,
  IGiftCard,
} from "api/rpc/2024-04/facilityAdmin/payment/giftCard";

import { showError, showSuccess } from "redux/actions/ui";
import { ICustomer } from "redux/reducers/models/customer";
import { IOrder } from "redux/reducers/models/order";
import {
  capitalize,
  customerErrorMessage,
  formatDate,
  isEmpty,
  parseLocalDateString,
  uppercase,
} from "helpers/Helpers";
import { formatGiftCardExpiryDate, isGiftCardExpired } from "helpers/giftCardExpiry";
import { GiftCardStatusBadge } from "./GiftCardsTable";
import { LocaleCurrency } from "helpers/Locale";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import { Badge } from "components/badge/Badge";
import Card from "components/card/Card";
import Page from "components/page/Page";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import Input from "components/form/input";
import Popup from "components/popup/Popup";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import ChangeCustomer from "components/changeCustomer/ChangeCustomer";
import Spin from "components/spin/spin";

import "./giftCard.scss";
import { IHistory } from "api/rpc/2022-09/facilityAdmin/payment/giftCard";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { GetCustomer, PostCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import DatePickerInput from "components/datePickerInput/DatePickerInput";

interface IGiftCardState {
  giftCard: {
    admin_id: number;
    balance: number;
    client_id: number;
    currency: string;
    customer_id: number;
    disabled_at: string;
    expires_at: string;
    id: number;
    last4: string;
    line_item_id: number;
    note: string;
    order_id: number;
    pin: string;
    status: string;
  };
  currentCustomer: ICustomer;
  order: IOrder;
  history: Array<IHistory>;
}

interface IGiftCardState {
  showChangeCustomerModal: boolean;
  customerQuery: string;
  selectedCustomer: ICustomer;
  customerSearching: boolean;
  customerSearchResult: Array<ICustomer>;
  showDisableModal: boolean;
  showEnableModal: boolean;
  enableExpiryDate: Date;
  showEmailModal: boolean;
  emailInput: string;
  emailAddressValid: IInputField;
}

interface IInputField {
  isDirty: boolean;
  isValid: boolean;
}

export default function GiftCard() {
  const history = useHistory();
  const { t } = useTranslation();
  const { giftCardCode } = useParams<{ giftCardCode: string }>();

  const dispatch = useAppDispatch();
  const permissions = useAppSelector(store => store.authStore.user?.permissions);
  const { facilityStore } = useAppSelector(store => store);

  const { Option } = Select;

  const [giftCard, setGiftCard] = useState<IGiftCard>(undefined);
  const [giftCardLogs, setGiftCardLogs] = useState<IGiftCardLog[]>([]); // loading spinner
  const [customer, setCustomer] = useState<ICustomer>(undefined);
  const [order, setOrder] = useState<IOrder>(undefined);

  const [state, setState] = useState<IGiftCardState>({
    giftCard: null,
    currentCustomer: null,
    order: null,
    history: [],
    showChangeCustomerModal: false,
    customerQuery: "",
    selectedCustomer: null,
    customerSearching: false,
    customerSearchResult: [],
    showDisableModal: false,
    showEnableModal: false,
    enableExpiryDate: new Date(),
    showEmailModal: false,
    emailInput: "",
    emailAddressValid: { isDirty: false, isValid: false },
  });

  const [newCustomerState, setNewCustomerState] = useState<{ showNewCustomerModal: boolean }>({
    showNewCustomerModal: false,
  });

  const [editGiftCardState, setEditGiftCardState] = useState<{
    showEditGiftCardModal: boolean;
    giftCard: IGiftCard;
    expiryDate: Date;
  }>({
    showEditGiftCardModal: false,
    giftCard: null,
    expiryDate: undefined,
  });

  // load original gift card data
  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadGiftCard(giftCardCode, source.token);
    return () => source.cancel();
  }, [giftCardCode]);

  // load async data associated too the loaded gift card
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!giftCard) {
      return;
    }

    if (giftCard.customer_id) {
      void loadCustomer(giftCard.customer_id, source.token);
    }

    if (giftCard.order_id) {
      void loadOrder(giftCard.order_id, source.token);
    }

    void loadHistory(giftCard.id, source.token);

    return () => source.cancel();
  }, [giftCard?.id, giftCard?.customer_id, giftCard?.order_id]);

  // Search customer when customer modal is open
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (state.customerQuery !== "") {
            try {
              setState(prevState => ({ ...prevState, customerSearching: true }));
              const customers = await searchCustomer();
              if (mounted) {
                setState(prevState => ({ ...prevState, customerSearchResult: customers, customerSearching: false }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setState(prevState => ({ ...prevState, customerSearchResult: [], customerSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [state.customerQuery]);

  async function loadGiftCard(code: string, token?: CancelToken) {
    const res = await GetGiftCard({ code: code }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK || res.data.length === 0) {
      dispatch(showError(t("secure.facility.order.gift_card.001")));
      history.push("/admin/giftcards");
      return;
    }

    setGiftCard(res.data[0]);
  }

  async function loadHistory(gift_card_id: number, token?: CancelToken) {
    if (giftCardLogs !== undefined) {
      setGiftCardLogs(undefined);
    }
    const historyRes = await GetGiftCardHistory({ gift_card_id }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (historyRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.042")));
      setGiftCardLogs([]);
      return;
    }

    const sortedHistory = historyRes.data?.sort((prev, next) =>
      new Date(prev.updated_at) < new Date(next.updated_at)
        ? 1
        : new Date(prev.updated_at) > new Date(next.updated_at)
        ? -1
        : 0,
    );

    setGiftCardLogs(sortedHistory);
  }

  async function loadCustomer(customer_id: number, token?: CancelToken) {
    const customerRes = await GetCustomer({ id: customer_id }, false, token);
    if (token && token.reason) {
      return;
    }
    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.002")));
      return;
    }

    setCustomer(customerRes.data[0]);
  }

  async function loadOrder(orderId: number, token?: CancelToken) {
    const orderRes = await GetOrder({ id: orderId }, false, token);
    if (token && token.reason) {
      return;
    }
    if (orderRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.003")));
      return;
    }

    setOrder(orderRes.data[0] as IOrder);
  }

  async function searchCustomer() {
    const customerRes = await GetCustomer({ search: state.customerQuery }, false);
    if (customerRes.status !== StatusCode.OK) {
      return [];
    }
    return customerRes.data;
  }

  async function createNewCustomer(customerInfo: ICustomerInfoState) {
    if (
      !customerInfo.firstName ||
      !customerInfo.lastName ||
      (!customerInfo.emailAddress && !customerInfo.phoneNumber)
    ) {
      dispatch(showError(t("secure.facility.order.gift_card.004")));
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber ?? null,
        email: customerInfo.emailAddress ?? null,
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(customerErrorMessage(t, customerRes?.data?.message as string)));
      return;
    }
    ReactDOM.unstable_batchedUpdates(() => {
      setNewCustomerState(prevState => ({
        ...prevState,
        showNewCustomerModal: false,
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      }));
      setState(prevState => ({ ...prevState, selectedCustomer: customerRes.data }));
    });
    dispatch(showSuccess(t("secure.facility.order.gift_card.006")));
  }

  async function handleGiftCardPrint() {
    const printRes = await PrintGiftCard({ id: giftCard?.id }, true);
    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.007")));
      return;
    }
    await loadHistory(giftCard.id);
    window.open().document.write(printRes.data as string);
  }

  async function assignCustomer() {
    const updateGiftCardRes = await PutGiftCard(
      { code: giftCardCode, customer_id: state.selectedCustomer?.id, note: "hey" },
      true,
    );
    if (updateGiftCardRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.008")));
      return;
    }
    dispatch(showSuccess(t("secure.facility.order.gift_card.009")));
    void closeChangeCustomerModal();

    await loadGiftCard(giftCardCode);
  }

  async function disableGiftCard() {
    const disableRes = await PutDisableGiftCard({ id: giftCard.id }, true);
    if (disableRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.010")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.gift_card.011")));
    setState(prevState => ({ ...prevState, showDisableModal: false }));
    await loadGiftCard(giftCardCode);
    await loadHistory(giftCard.id);
  }

  async function enableGiftCard() {
    const params: { id: number; expires_at?: string } = { id: giftCard.id };

    if (needsExpiryDateOnEnable) {
      params.expires_at = formatDate(state.enableExpiryDate);
    }

    const enableRes = await PutEnableGiftCard(params, true);
    if (enableRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error re-enabling gift card")));
      return;
    }

    dispatch(showSuccess(t("Gift card re-enabled successfully")));
    setState(prevState => ({ ...prevState, showEnableModal: false }));
    await loadGiftCard(giftCardCode);
    await loadHistory(giftCard.id);
  }

  async function handleEmailConfirm() {
    const updateRes = await PutGiftCardEmail({ gift_card_id: giftCard.id, customer_email: state.emailInput }, true);
    if (updateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.043")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.gift_card.044")));
    setState(prevState => ({ ...prevState, showEmailModal: false }));
  }

  async function handleGiftCardLogPrint() {
    const printRes = await PrintGiftCardLog({ id: giftCard.id, last4: giftCard.last4 }, true);
    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_card.045")));
      return;
    }
    window.open().document.write(printRes.data as string);
  }

  function closeChangeCustomerModal() {
    setState(prevState => ({
      ...prevState,
      showChangeCustomerModal: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
    }));
  }

  function closeEmailModal() {
    setState(prevState => ({
      ...prevState,
      showEmailModal: false,
    }));
  }
  function handleCustomerSearch(query: string) {
    setState(prevState => ({ ...prevState, customerQuery: query }));
  }

  function handleCustomerSelection(id: number, customer: ICustomer) {
    setState(prevState => ({ ...prevState, selectedCustomer: customer }));
  }

  function openNewCustomerSheet() {
    setNewCustomerState(prevState => ({
      ...prevState,
      showNewCustomerModal: true,
    }));
  }

  function handleEmailInput(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    console.log("target", e.target);
    setState(prevState => ({
      ...prevState,
      emailInput: value,
      emailAddressValid: id === "emailAddress" ? isEmailValid(value) : prevState.emailAddressValid,
    }));
  }

  function isEmailValid(email: string) {
    return email === "" ? { isDirty: false, isValid: false } : { isDirty: true, isValid: validator.isEmail(email) };
  }

  async function handleEditExpiryDate() {
    const params = {
      id: giftCard?.id,
      expires_at: formatDate(editGiftCardState.expiryDate),
      code: giftCardCode,
    };

    const updateGiftCardRes = await PutGiftCard(params, true);
    if (updateGiftCardRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating expiry date")));
      return;
    }
    dispatch(showSuccess(t("Expiry date updated successfully")));
    setEditGiftCardState(prev => ({ ...prev, showEditGiftCardModal: false, giftCard: null, expiryDate: undefined }));
    await loadGiftCard(giftCardCode);
  }

  const isDisabled = giftCard?.status === "disabled";
  const isExpired = isGiftCardExpired(giftCard?.expires_at, giftCard?.status);
  const canReEnable = isDisabled || giftCard?.status === "expired";
  const facilityRequiresExpiry = facilityStore?.teeSheetSettings?.gift_card_expiry !== "no_expiry";
  const needsExpiryDateOnEnable =
    canReEnable && (giftCard?.status === "expired" || isExpired) && facilityRequiresExpiry;

  return (
    <>
      <Page
        narrow
        splitLayout
        title={t("Gift Card")}
        subtitle={giftCardCode}
        breadcrumbs={[{ prefix: true, label: t("Back to Gift Cards"), url: "/admin/giftcards" }]}
        multipleActionDropdownAction={{
          label: t("Options"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("Print"),
                icon: "print",
                handler: () => handleGiftCardPrint(),
                disabled: giftCard?.status === "disabled" || giftCard?.disabled_at ? true : false,
              },
              {
                type: "handler",
                label: t("Print Log"),
                icon: "print-magnifying-glass",
                handler: () => handleGiftCardLogPrint(),
              },
              {
                type: "handler",
                label: canReEnable ? t("Re-enable") : t("Disable"),
                icon: canReEnable ? "check" : "ban",
                handler: () =>
                  setState(prevState => ({
                    ...prevState,
                    ...(canReEnable
                      ? {
                          showEnableModal: true,
                          enableExpiryDate: giftCard?.expires_at
                            ? parseLocalDateString(giftCard.expires_at) ?? new Date()
                            : new Date(),
                        }
                      : { showDisableModal: true }),
                  })),
                disabled: canReEnable ? !permissions?.gift_cards_enable : false,
              },
              {
                type: "handler",
                label: t("Email"),
                icon: "envelope",
                handler: () =>
                  setState(prevState => ({ ...prevState, emailInput: customer?.email, showEmailModal: true })),
                disabled: giftCard?.status === "disabled",
              },

              {
                type: "handler",
                label: t("Edit expiry date"),
                icon: "edit",
                handler: () =>
                  setEditGiftCardState(prevState => ({
                    ...prevState,
                    showEditGiftCardModal: true,
                    giftCard: giftCard,
                    expiryDate: giftCard?.expires_at
                      ? parseLocalDateString(giftCard.expires_at) ?? new Date()
                      : new Date(),
                  })),
                disabled: giftCard?.status === "disabled",
              },
            ],
          },
        }}
      >
        {giftCard && (
          <>
            <Page.Section twoThirds>
              {/* Gift Card Details Card*/}
              <Card title={t("Gift Card Details")}>
                <Card.Section>
                  <div className="orders-giftcard">
                    <div className="giftcard-column">
                      <div className="giftcard-row">
                        <p className="giftcard-details-title">{t("Balance")}</p>
                        <p className="giftcard-details-value">
                          <LocaleCurrency currency={giftCard?.currency} amount={giftCard?.balance} />
                        </p>
                      </div>
                      <div className="giftcard-row">
                        <p className="giftcard-details-title">{t("Currency")}</p>
                        <p className="giftcard-details-value">{uppercase(giftCard?.currency)}</p>
                      </div>
                      <div className="giftcard-row">
                        <p className="giftcard-details-title">{t("Status")}</p>
                        <GiftCardStatusBadge size="small" status={giftCard?.status} expiresAt={giftCard?.expires_at} />
                      </div>
                      <div className="giftcard-row">
                        <p className="giftcard-details-title">{t("PIN")}</p>
                        <p className="giftcard-details-value">{giftCard?.pin}</p>
                      </div>
                      {giftCard?.disabled_at && (
                        <div className="giftcard-row">
                          <p className="giftcard-details-title">{t("Disabled at")}</p>
                          <p className="giftcard-details-value">
                            {moment.utc(giftCard?.disabled_at).local().format("LLL")}
                          </p>
                        </div>
                      )}
                      {giftCard?.expires_at && (
                        <div className="giftcard-row">
                          <p className="giftcard-details-title">{t("Expiry Date")}</p>
                          <p className="giftcard-details-value">{formatGiftCardExpiryDate(giftCard?.expires_at)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card.Section>
              </Card>

              <Card title={t("Recent Transactions")}>
                {/* Transaction Card */}

                {!giftCardLogs ? (
                  <Card.Section>
                    <div style={{ overflow: "hidden", height: "50px" }}>
                      <Spin />
                    </div>
                  </Card.Section>
                ) : (
                  giftCardLogs?.map((transaction, index: number) => {
                    let title = "";
                    const isTransactionVoided = transaction?.voided_at !== null;
                    switch (transaction.log_type) {
                      case "print":
                        title = t("Receipt printed for gift card");
                        break;
                      case "merged_to":
                        title = transaction.log_description;
                        break;
                      case "merged_from":
                        title = transaction.log_description;
                        break;
                      case "disabled":
                        title = t("Gift card was disabled");
                        break;
                      case "enabled":
                        title = t("Gift card was enabled");
                        break;
                      default:
                        title = `${t("Order")} #${transaction.transaction?.order?.number ?? ""} ${
                          isTransactionVoided ? " - " + t("Voided") : ""
                        }`;
                        break;
                    }
                    return (
                      <Card.Section
                        title={title}
                        key={index}
                        sectionTitleActions={
                          transaction?.log_type === "credit" || transaction?.log_type === "debit"
                            ? [
                                {
                                  action: () =>
                                    history.push("/admin/order/" + String(transaction?.transaction?.order_id)),
                                  content: t("View"),
                                },
                              ]
                            : []
                        }
                      >
                        <div className="column">
                          {transaction?.log_type !== "print" &&
                            transaction?.log_type !== "disabled" &&
                            transaction?.log_type !== "enabled" && (
                              <span
                                className={classNames("column-data", {
                                  "transaction-debit": transaction?.log_type === "debit",
                                  "transaction-credit": transaction?.log_type === "credit",
                                  "line-through": isTransactionVoided,
                                })}
                              >
                                {transaction?.log_type === "debit"
                                  ? "-"
                                  : transaction?.log_type === "credit"
                                  ? "+"
                                  : ""}
                                <LocaleCurrency
                                  currency={transaction?.transaction?.currency}
                                  amount={transaction?.change ?? ""}
                                />
                              </span>
                            )}
                          <span> {moment(transaction?.updated_at).format("LLL")}</span>
                        </div>
                        <span className="thin-separator"></span>
                      </Card.Section>
                    );
                  })
                )}

                {/* End Transaction Card*/}
              </Card>
            </Page.Section>

            <Page.Section oneThird>
              <Card
                title={t("Customer")}
                titleActions={
                  giftCard?.status === "disabled" || giftCard?.disabled_at
                    ? undefined
                    : [
                        {
                          action: () => setState(prevState => ({ ...prevState, showChangeCustomerModal: true })),
                          content: t("Edit"),
                        },
                      ]
                }
              >
                {/* Customer Card */}
                <Card.Section>
                  {customer ? (
                    <div>
                      <p className="text-lg">{customer?.full_name}</p>
                      <p className="text-lg">{customer?.email}</p>
                      <p className="text-lg">{customer?.phone}</p>
                      <p className="text-lg">{customer?.customer_type}</p>
                    </div>
                  ) : (
                    <p>{t("secure.facility.order.gift_card.023")}</p>
                  )}
                </Card.Section>
                {/* End Customer Card */}
              </Card>

              {order && (
                <Card
                  title={`${t("Order")} ${order.name}`}
                  titleActions={[
                    {
                      action: () =>
                        giftCard.order_id ? history.push("/admin/order/" + String(giftCard.order_id)) : {},
                      content: t("View"),
                    },
                  ]}
                >
                  {/* Gift Card Order Card */}
                  <Card.Section>
                    <div className="orders-giftcard">
                      <div className="giftcard-column">
                        <div className="giftcard-row">
                          <p className="giftcard-details-title">{t("Financial Status")}</p>
                          <Badge size="small" type="success">
                            {capitalize(order.financial_status ?? "")}
                          </Badge>
                        </div>
                        <div className="giftcard-row">
                          <p className="giftcard-details-title">{t("Total")}</p>
                          <LocaleCurrency currency={order?.currency} amount={order?.total_price} />
                        </div>
                      </div>
                    </div>
                  </Card.Section>
                  {/* Gift Card Order Card End */}
                </Card>
              )}
            </Page.Section>
          </>
        )}
      </Page>

      <ChangeCustomer
        title={t("secure.facility.order.gift_card.052")}
        currentCustomerTitle={t("secure.facility.order.gift_card.053")}
        newCustomerTitle={t("secure.facility.order.gift_card.054")}
        okText={t("secure.facility.order.gift_card.028")}
        open={state.showChangeCustomerModal}
        changeCustomerState={state}
        searching={state.customerSearching}
        searchResults={state.customerSearchResult}
        newCustomerActive={newCustomerState.showNewCustomerModal}
        onCancel={closeChangeCustomerModal}
        onOk={assignCustomer}
        handleSearchQueryChange={handleCustomerSearch}
        handleCustomerSelection={handleCustomerSelection}
        handleRemoveSelectedCustomer={() =>
          setState(prevState => ({
            ...prevState,
            selectedCustomer: null,
            customerSearchResult: [],
            customerQuery: "",
          }))
        }
        handleCreateNewCustomer={createNewCustomer}
        handleCloseCreateNewCustomer={closeChangeCustomerModal}
        handleNewCustomerActive={() => setNewCustomerState(prevState => ({ ...prevState, showNewCustomerModal: true }))}
      />

      {/* Golfer Card */}
      <Sheet
        title={t("secure.facility.order.gift_card.027")}
        open={state.showChangeCustomerModal}
        size="small"
        closable
        onCancel={closeChangeCustomerModal}
        onOk={assignCustomer}
        okText={t("secure.facility.order.gift_card.028")}
        overflow
        okDisabled={!state.selectedCustomer ? true : false}
      >
        {customer !== null ? (
          <div>
            <span>{t("secure.facility.order.gift_card.029")}</span>
            <GolferCard
              email={customer?.email}
              name={customer?.full_name}
              memberCode={customer?.member_code}
              customerType={customer?.customer_type}
              phone={customer?.phone}
            />

            <FontAwesomeIcon className="orders-giftcard-arrow-icon" icon={["far", "arrow-circle-down"]} size="2x" />
          </div>
        ) : null}
        {state.selectedCustomer ? (
          <GolferCard
            closable
            removeGolfer={() =>
              setState(prevState => ({
                ...prevState,
                selectedCustomer: null,
                customerSearchResult: [],
                customerQuery: "",
              }))
            }
            email={state?.selectedCustomer?.email}
            name={state?.selectedCustomer?.full_name}
            memberCode={state?.selectedCustomer?.member_code}
            customerType={state?.selectedCustomer?.customer_type}
            phone={state?.selectedCustomer?.phone}
          />
        ) : (
          <Select
            showSearch
            onSearch={(query: string) => setState(prevState => ({ ...prevState, customerQuery: query }))}
            onChange={(id: number, customer: ICustomer) =>
              setState(prevState => ({ ...prevState, selectedCustomer: customer }))
            }
            placeholder={t("secure.facility.order.gift_card.030")}
            allowClear
            searchValue={state.customerQuery}
            showDropDownOnFocus={true}
            searching={state.customerSearching}
          >
            <div className="ui-select-dropdown-list-item" onClick={() => openNewCustomerSheet()}>
              <p>{t("secure.facility.order.gift_card.031")}</p>
            </div>
            {state.customerSearchResult?.map((customer, index) => {
              return (
                <Option key={index} value={customer.id} name={customer.full_name} extraValues={customer}>
                  <div className="flex justify-between">
                    <div>
                      <div className="text-semibold text-lg">{customer?.full_name}</div>
                      <div className="text-sm text-gray-500">{customer.customer_type}</div>
                      <div className="text-sm text-gray-500">{customer.email}</div>
                      <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                    </div>

                    <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                  </div>
                </Option>
              );
            })}
          </Select>
        )}
      </Sheet>

      <NewCustomer
        newCustomerSheetActive={newCustomerState.showNewCustomerModal}
        onCancel={() =>
          setNewCustomerState(prevState => ({
            ...prevState,
            showNewCustomerModal: false,
          }))
        }
        onOk={createNewCustomer}
        searchValue={state.customerQuery}
        // handleNewCustomerActive={openNewCustomerSheet}
      />

      {/* End Golfer Card */}

      <Popup
        type="warning"
        open={state.showDisableModal}
        title={t("secure.facility.order.gift_card.038")}
        description={t("secure.facility.order.gift_card.039")}
        closable
        onOk={disableGiftCard}
        onCancel={() => setState(prevState => ({ ...prevState, showDisableModal: false }))}
        okText={t("secure.facility.order.gift_card.040")}
      />

      <Popup
        type="info"
        open={state.showEnableModal}
        title={t("Re-enable Gift Card?")}
        description={t(
          "Re-enabling a gift card restores it to valid status so it can be used towards a future purchase.",
        )}
        closable
        onOk={enableGiftCard}
        onCancel={() => setState(prevState => ({ ...prevState, showEnableModal: false }))}
        okText={t("Re-enable")}
        okDisabled={needsExpiryDateOnEnable && !state.enableExpiryDate}
      >
        {needsExpiryDateOnEnable ? (
          <div className="mt-2">
            <DatePickerInput
              showQuickOptions
              months={1}
              position="left"
              startingDate={state.enableExpiryDate}
              setStartingDate={(date: Date) => setState(prevState => ({ ...prevState, enableExpiryDate: date }))}
            />
          </div>
        ) : null}
      </Popup>

      <Sheet
        title={t("secure.facility.order.gift_card.055")}
        open={state.showEmailModal}
        size="small"
        closable
        onCancel={closeEmailModal}
        onOk={handleEmailConfirm}
        okText={t("secure.facility.order.gift_card.056")}
        okDisabled={
          (state?.emailAddressValid?.isDirty && !state?.emailAddressValid?.isValid) || isEmpty(state?.emailInput)
        }
      >
        <Input
          value={state?.emailInput || ""}
          placeholder={t("secure.facility.order.gift_card.057")}
          id="emailAddress"
          onChange={handleEmailInput}
          error={state?.emailAddressValid?.isDirty && !state?.emailAddressValid?.isValid}
        />
      </Sheet>

      <Popup
        open={editGiftCardState?.showEditGiftCardModal}
        title={t("Edit Expiry Date")}
        description={t("Please check local laws before setting the expiry date.")}
        onOk={handleEditExpiryDate}
        okText={t("Save")}
        cancelText={t("Cancel")}
        onCancel={() =>
          setEditGiftCardState(prev => ({
            ...prev,
            showEditGiftCardModal: false,
            giftCard: null,
            expiryDate: undefined,
          }))
        }
        type="warning"
      >
        <div className="mt-2">
          <DatePickerInput
            showQuickOptions
            months={1}
            position="left"
            startingDate={editGiftCardState?.expiryDate}
            setStartingDate={(date: Date) =>
              setEditGiftCardState(prev => ({
                ...prev,
                expiryDate: date,
              }))
            }
          />
        </div>
      </Popup>
    </>
  );
}
